<script setup lang="ts">
// ================================
// LE GRAPHIQUE « PUISSANCES PAR POSTE » (D14, décisions v1.2 §B, 18/10/2026).
//
// Ce que le lecteur doit vivre (revue du 19/10/2026, N1) : poste par poste,
// toujours deux barres couchées depuis zéro à la même échelle (la puissance
// actuelle en clair, la recommandée en plein, égales quand rien ne change),
// la valeur de chacune écrite en gras au bout, à l'extérieur, le trait de la
// puissance maximale atteinte qui les traverse, sa valeur écrite une fois
// au-dessus ; la réserve en vert dans la barre, le dépassement en rouge dans
// son prolongement, sans étiquette chiffrée (bleu clair : la marge de
// sécurité de la barre recommandée). SVG dessiné à la main : le modèle est dans
// `optim-turpe-v2.graphique.ts`, la géométrie dans `…graphique.dessin.ts` ;
// ce composant ne fait que poser les primitives, dans l'ordre barres, trait,
// textes (greffe n° 1 : aucun texte rayé).
// ================================

// ================================
// IMPORTS
// ================================
import { computed, onBeforeUnmount, onMounted, ref, useId } from "vue"
import {
    infoRelevee,
    modeleGraphique,
} from "@modules/courtage/utils/optim-turpe-v2.graphique"
import type {
    ContexteGraphique,
    SigneLegende,
} from "@modules/courtage/utils/optim-turpe-v2.graphique"
import {
    COULEUR,
    dessinerGraphique,
} from "@modules/courtage/utils/optim-turpe-v2.graphique.dessin"
import type { TexteDessin } from "@modules/courtage/utils/optim-turpe-v2.graphique.dessin"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

// ================================
// TYPES & PROPS
// ================================
type Props = {
    etude: OptimTurpeV2Etude
    /** Le scénario affiché n'est pas le conseil (cas 8, « Option chiffrée »). */
    optionChiffree?: boolean
    /** L'étude de l'autre scénario de la paire : repère creux (cas 8) ou pied (cas 3). */
    autre?: OptimTurpeV2Etude | null
    /** Le scénario de l'étude affichée : « sans » (Couvrir) ou « avec » (Assumer). */
    scenario?: "sans" | "avec" | null
    /**
     * Largeur imposée (px), pour les stories et les tests ; sinon la largeur
     * mesurée du conteneur (1 040 px par défaut avant la première mesure).
     */
    largeur?: number | null
}
const props = withDefaults(defineProps<Props>(), {
    optionChiffree: false,
    autre: null,
    scenario: null,
    largeur: null,
})

// ================================
// LA LARGEUR DISPONIBLE
// ================================
/** Le conteneur, mesuré pour dessiner à la largeur réelle (pas de mise à l'échelle des textes). */
const racine = ref<HTMLElement | null>(null)
/** La largeur mesurée ; 1 040 px (la carte à 1 440 px) tant qu'aucune mesure n'est faite. */
const largeurMesuree = ref(1040)
let observateur: ResizeObserver | null = null

onMounted(() => {
    if (!racine.value || typeof ResizeObserver === "undefined") return
    observateur = new ResizeObserver((entrees) => {
        const l = Math.floor(entrees[0]?.contentRect.width ?? 0)
        if (l > 0) largeurMesuree.value = l
    })
    observateur.observe(racine.value)
})
onBeforeUnmount(() => observateur?.disconnect())

/** La largeur de dessin : imposée, sinon mesurée. */
const getLargeur = computed(() => props.largeur ?? largeurMesuree.value)

// ================================
// LE MODÈLE ET LE DESSIN
// ================================
/** Quoi dessiner : postes, barres, écarts, légende, pied. */
const getModele = computed(() => {
    const contexte: ContexteGraphique = {
        optionChiffree: props.optionChiffree,
        autre: props.autre,
        scenario: props.scenario,
    }
    return modeleGraphique(props.etude, contexte)
})
/** Où le dessiner, à la largeur disponible. */
/** I39 : ce que veut dire « relevée », en info-bulle de la mention (choix 2). */
const getInfoRelevee = computed(() =>
    infoRelevee(props.etude.compteur?.segment)
)
const getDessin = computed(() =>
    dessinerGraphique(getModele.value, getLargeur.value)
)

/** Identifiant unique par instance : le titre du SVG. */
const id = useId()

/** Le style d'un texte : taille, graisse, couleur, halo peint sous le texte. */
function styleTexte(t: TexteDessin): Record<string, string> {
    const style: Record<string, string> = {
        fontSize: `${t.taille}px`,
        fontWeight: String(t.graisse),
        fill: t.couleur,
        fontVariantNumeric: "tabular-nums",
    }
    if (t.halo) {
        style.paintOrder = "stroke"
        style.stroke = t.halo
        style.strokeWidth = "3px"
        style.strokeLinejoin = "round"
    }
    return style
}

/** L'échantillon de chaque signe de la légende, en classes Tailwind sur jetons. */
const CLASSES_LEGENDE: Record<SigneLegende, string> = {
    aujourdhui: "h-2 w-3.5 rounded-[1px] bg-primary-200",
    propose: "h-2 w-3.5 rounded-[1px] bg-primary-700",
    repere: "size-2.5 rounded-full border-2 border-primary-700 bg-white",
    pic: "h-3.5 w-[3px] rounded-full bg-primary-900",
    pic_pointille: "h-3.5 w-0 border-l-[3px] border-dashed border-primary-900",
    reserve: "h-2 w-3.5 rounded-[1px] bg-[var(--p-green-600)]",
    marge: "h-2 w-3.5 rounded-[1px] bg-primary-300",
    depassement: "h-2 w-3.5 rounded-[1px] bg-[var(--p-red-600)]",
    raccordement: "h-3.5 w-0 border-l-2 border-dotted border-surface-600",
    raccordement_ecarte:
        "h-3.5 w-0 border-l-2 border-dotted border-[var(--p-orange-500)]",
}

/** La barre pleine « Vos puissances » ou « Option chiffrée » est en bleu moyen. */
const getClasseProposeLegende = computed(() =>
    getModele.value.colonne === "Recommandé"
        ? CLASSES_LEGENDE.propose
        : "h-2 w-3.5 rounded-[1px] bg-primary-500"
)
</script>

<template>
    <div
        ref="racine"
        class="optim-turpe-v2-graphique-puissances flex w-full flex-col gap-2 [-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
        data-testid="optim-turpe-v2-graphique-puissances"
        :data-etroit="getDessin?.etroit ? '1' : '0'"
    >
        <template v-if="getDessin">
            <!-- ================================ -->
            <!-- LÉGENDE EN UNE LIGNE ET LIGNE DE MÉTA -->
            <!-- ================================ -->
            <p
                aria-hidden="true"
                class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-primary-600"
                data-testid="optim-turpe-v2-graphique-legende"
            >
                <template v-for="(el, i) in getModele.legende" :key="el.signe">
                    <span v-if="i > 0" class="text-primary-400">·</span>
                    <span
                        class="inline-flex items-center gap-1 whitespace-nowrap"
                    >
                        <span
                            class="inline-block shrink-0"
                            :class="
                                el.signe === 'propose'
                                    ? getClasseProposeLegende
                                    : CLASSES_LEGENDE[el.signe]
                            "
                        />
                        {{ el.texte }}
                    </span>
                </template>
            </p>
            <p
                class="text-xs text-surface-500"
                data-testid="optim-turpe-v2-graphique-meta"
            >
                {{ getModele.meta }}
            </p>

            <!-- ================================ -->
            <!-- LE GRAPHIQUE : barres, puis traits, puis textes -->
            <!-- ================================ -->
            <svg
                class="block h-auto max-w-full overflow-visible"
                :width="getDessin.largeur"
                :height="getDessin.hauteur"
                :viewBox="`0 0 ${getDessin.largeur} ${getDessin.hauteur}`"
                role="img"
                :aria-labelledby="`${id}-titre`"
                data-testid="optim-turpe-v2-graphique-svg"
                :data-hauteur="getDessin.hauteur"
            >
                <title :id="`${id}-titre`">Puissances par poste</title>
                <!-- Grille et axe, derrière tout. -->
                <g aria-hidden="true">
                    <line
                        v-for="(g, i) in getDessin.grille"
                        :key="`g${i}`"
                        :x1="g.x"
                        :x2="g.x"
                        :y1="g.y1"
                        :y2="g.y2"
                        :stroke="COULEUR.grille"
                        stroke-width="1"
                    />
                    <line
                        :x1="getDessin.axe.x1"
                        :x2="getDessin.axe.x2"
                        :y1="getDessin.axe.y"
                        :y2="getDessin.axe.y"
                        :stroke="COULEUR.axe"
                        stroke-width="1"
                    />
                </g>

                <!-- Les barres de chaque poste, chacune avec son texte alternatif. -->
                <g
                    v-for="poste in getDessin.postes"
                    :key="`b-${poste.cle}`"
                    role="group"
                    :aria-label="poste.titre"
                    data-testid="optim-turpe-v2-graphique-poste"
                    :data-poste="poste.cle"
                >
                    <g
                        v-for="barre in poste.barres"
                        :key="barre.nature"
                        role="img"
                        :aria-label="barre.texte"
                        data-testid="optim-turpe-v2-graphique-barre"
                        :data-nature="barre.nature"
                        :data-sens="barre.sens ?? undefined"
                    >
                        <title>{{ barre.texte }}</title>
                        <rect
                            v-for="(r, k) in barre.rects"
                            :key="k"
                            :x="r.x"
                            :y="r.y"
                            :width="r.l"
                            :height="r.h"
                            :fill="r.couleur"
                            :data-role="r.role"
                        />
                    </g>
                </g>

                <!-- Le raccordement, puis les traits du pic et les repères creux. -->
                <g aria-hidden="true">
                    <line
                        v-if="getDessin.raccordement?.ligne"
                        :x1="getDessin.raccordement.ligne.x"
                        :x2="getDessin.raccordement.ligne.x"
                        :y1="getDessin.raccordement.ligne.y1"
                        :y2="getDessin.raccordement.ligne.y2"
                        :stroke="
                            getDessin.raccordement.ecarte
                                ? COULEUR.raccordementEcarte
                                : COULEUR.raccordement
                        "
                        stroke-width="1.5"
                        stroke-dasharray="2 3"
                        data-testid="optim-turpe-v2-graphique-raccordement"
                        :data-ecarte="getDessin.raccordement.ecarte ? '1' : '0'"
                    />
                    <template
                        v-for="poste in getDessin.postes"
                        :key="`t-${poste.cle}`"
                    >
                        <line
                            v-if="poste.trait"
                            :x1="poste.trait.x"
                            :x2="poste.trait.x"
                            :y1="poste.trait.y1"
                            :y2="poste.trait.y2"
                            :stroke="COULEUR.trait"
                            stroke-width="3"
                            :stroke-linecap="
                                poste.trait.pointille ? 'butt' : 'round'
                            "
                            :stroke-dasharray="
                                poste.trait.pointille ? '3 3' : undefined
                            "
                            data-testid="optim-turpe-v2-graphique-trait"
                            :data-poste="poste.cle"
                            :data-pointille="poste.trait.pointille ? '1' : '0'"
                        />
                        <circle
                            v-if="poste.repere"
                            :cx="poste.repere.cx"
                            :cy="poste.repere.cy"
                            r="4.5"
                            :fill="COULEUR.blanc"
                            :stroke="COULEUR.recommande"
                            stroke-width="2"
                            data-testid="optim-turpe-v2-graphique-repere"
                        />
                    </template>
                </g>

                <!-- Les textes, après les traits, avec halo. -->
                <g aria-hidden="true">
                    <text
                        v-for="(t, i) in getDessin.graduations"
                        :key="`a${i}`"
                        :x="t.x"
                        :y="t.y"
                        :text-anchor="t.ancre"
                        :style="styleTexte(t)"
                        data-testid="optim-turpe-v2-graphique-graduation"
                    >
                        {{ t.texte }}
                    </text>
                    <text
                        v-if="getDessin.raccordement"
                        :x="getDessin.raccordement.texte.x"
                        :y="getDessin.raccordement.texte.y"
                        :text-anchor="getDessin.raccordement.texte.ancre"
                        :style="styleTexte(getDessin.raccordement.texte)"
                        data-testid="optim-turpe-v2-graphique-raccordement-texte"
                    >
                        {{ getDessin.raccordement.texte.texte }}
                    </text>
                    <template
                        v-for="poste in getDessin.postes"
                        :key="`x-${poste.cle}`"
                    >
                        <text
                            v-for="(t, i) in poste.libelles"
                            :key="`l${i}`"
                            :x="t.x"
                            :y="t.y"
                            :text-anchor="t.ancre"
                            :style="styleTexte(t)"
                            data-testid="optim-turpe-v2-graphique-libelle"
                        >
                            {{ t.texte }}
                        </text>
                        <g v-if="poste.relevee">
                            <title v-if="getInfoRelevee">
                                {{ getInfoRelevee }}
                            </title>
                            <text
                                :x="poste.relevee.x"
                                :y="poste.relevee.y"
                                :text-anchor="poste.relevee.ancre"
                                :style="styleTexte(poste.relevee)"
                                data-testid="optim-turpe-v2-graphique-relevee"
                            >
                                {{ poste.relevee.texte }}
                            </text>
                        </g>
                        <text
                            v-if="poste.pic"
                            :x="poste.pic.x"
                            :y="poste.pic.y"
                            :text-anchor="poste.pic.ancre"
                            :style="styleTexte(poste.pic)"
                            data-testid="optim-turpe-v2-graphique-pic"
                        >
                            {{ poste.pic.texte }}
                        </text>
                        <text
                            v-for="barre in poste.barres"
                            :key="`v-${barre.nature}`"
                            :x="barre.valeur.x"
                            :y="barre.valeur.y"
                            :text-anchor="barre.valeur.ancre"
                            :style="styleTexte(barre.valeur)"
                            data-testid="optim-turpe-v2-graphique-valeur"
                            :data-nature="barre.nature"
                        >
                            {{ barre.valeur.texte }}
                        </text>
                    </template>
                </g>
            </svg>

            <!-- ================================ -->
            <!-- LE PIED : ce que le dessin ne dit pas seul -->
            <!-- ================================ -->
            <p
                v-for="(phrase, i) in getModele.pied"
                :key="i"
                class="text-sm text-surface-600"
                data-testid="optim-turpe-v2-graphique-pied"
            >
                {{ phrase }}
            </p>
        </template>
    </div>
</template>
